(() => {
  if (customElements.get('btm-shipping-estimate')) return;

  class BTMShippingEstimate extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.country = this.querySelector('[data-shipping-country]');
      this.province = this.querySelector('[data-shipping-province]');
      this.provinceWrap = this.querySelector('[data-shipping-province-wrap]');
      this.button = this.querySelector('[data-shipping-submit]');
      this.status = this.querySelector('[data-shipping-status]');
      this.rates = this.querySelector('[data-shipping-rates]');
      this.version = 0;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      if (Array.from(this.country.options).some(option => option.value === this.dataset.country)) {
        this.country.value = this.dataset.country;
      }
      this.updateProvinces();
      this.country.addEventListener('change', () => this.updateProvinces(), options);
      this.addEventListener('input', () => this.invalidate(), options);
      this.addEventListener('change', () => this.invalidate(), options);
      this.button.addEventListener('click', () => this.estimate(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidate());
      }
    }

    disconnectedCallback() {
      this.invalidate();
      this.listeners?.abort();
      this.unsubscribe?.();
      this.connected = false;
    }

    updateProvinces() {
      let provinces = [];
      try {
        provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]');
      } catch (_) {}
      this.province.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.dataset.chooseProvince;
      this.province.append(placeholder);
      for (const [value, label] of provinces) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        this.province.append(option);
      }
      this.provinceWrap.hidden = provinces.length === 0;
    }

    async checkResponse(response) {
      if (!response.ok) throw new Error('Shipping estimate unavailable');
    }

    invalidate() {
      this.version = (this.version || 0) + 1;
      this.request?.abort();
      if (this.button) this.button.disabled = false;
      if (this.rates) {
        const hadResult = !this.rates.hidden || this.status.textContent;
        this.rates.replaceChildren();
        this.rates.hidden = true;
        this.status.textContent = hadResult ? this.dataset.stale : '';
      }
      this.removeAttribute('aria-busy');
    }

    async estimate() {
      if (this.button.disabled) return;
      this.invalidate();
      if (!this.country.value) {
        this.status.textContent = this.dataset.choose;
        this.country.focus();
        return;
      }
      if (!this.provinceWrap.hidden && !this.province.value) {
        this.status.textContent = this.dataset.chooseProvince;
        this.province.focus();
        return;
      }
      const version = this.version;
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 20000);
      const parameters = new URLSearchParams({
        'shipping_address[country]': this.country.value
      });
      if (!this.provinceWrap.hidden) parameters.set('shipping_address[province]', this.province.value);
      const root = this.dataset.root || '/';
      const base = root.endsWith('/') ? root : root + '/';
      const requestOptions = { credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } };
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        const prepared = await fetch(base + 'cart/prepare_shipping_rates.json?' + parameters, { ...requestOptions, method: 'POST' });
        await this.checkResponse(prepared);
        let result = null;
        for (let attempt = 0; attempt < 15; attempt += 1) {
          if (version !== this.version || !this.isConnected) return;
          if (request.signal.aborted) throw new Error('Shipping estimate timed out');
          const response = await fetch(base + 'cart/async_shipping_rates.json?' + parameters, requestOptions);
          await this.checkResponse(response);
          result = await response.json();
          if (result !== null) break;
          await new Promise(resolve => setTimeout(resolve, 800));
        }
        if (version !== this.version || !this.isConnected) return;
        if (request.signal.aborted) throw new Error('Shipping estimate timed out');
        if (!Array.isArray(result?.shipping_rates)) throw new Error('Shipping estimate unavailable');
        this.renderRates(result.shipping_rates);
      } catch (error) {
        if (version === this.version && this.isConnected) {
          this.status.textContent = this.dataset.error;
        }
      } finally {
        clearTimeout(timeout);
        if (version === this.version) {
          this.button.disabled = false;
          this.removeAttribute('aria-busy');
        }
      }
    }

    renderRates(rates) {
      if (!rates.length) {
        this.status.textContent = this.dataset.empty;
        return;
      }
      const currency = this.dataset.currency;
      if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Missing currency');
      const locale = document.documentElement.lang || 'en';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fragment = document.createDocumentFragment();
      for (const rate of rates) {
        const amount = Number(rate.price);
        if (rate.price === null || rate.price === '' || !Number.isFinite(amount) || amount < 0 || (rate.currency && rate.currency !== currency)) {
          throw new Error('Invalid shipping amount');
        }
        const row = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = rate.presentment_name || rate.name || '';
        const price = document.createElement('strong');
        price.textContent = formatter.format(amount);
        row.append(name, price);
        fragment.append(row);
      }
      this.rates.replaceChildren(fragment);
      this.rates.hidden = false;
      this.status.textContent = this.dataset.ready;
    }
  }

  customElements.define('btm-shipping-estimate', BTMShippingEstimate);
})();


